import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { StoreContext, type StoreData } from "./StoreContext";
import { getApplicationMetadataFromUrl } from "../utils/applicationMetadata";

function getInitialStoreData(): StoreData {
  return {
    applicationMetadata: getApplicationMetadataFromUrl(),
  };
}

export function StoreProvider({ children }: PropsWithChildren) {
  const [data, setData] = useState<StoreData>(getInitialStoreData);
  const [isInitialNavigation, setIsInitialNavigation] = useState(true);

  const updateData = useCallback((updates: Partial<StoreData>) => {
    setData((prevData) => {
      const hasChanges = Object.entries(updates).some(
        ([key, value]) => prevData[key as keyof StoreData] !== value
      );

      return hasChanges ? { ...prevData, ...updates } : prevData;
    });
  }, []);

  const replaceData = useCallback((nextData: StoreData) => {
    setData((prevData) => ({
      ...nextData,
      applicationMetadata: nextData.applicationMetadata ?? prevData.applicationMetadata,
    }));
  }, []);

  const markInitialRouteResolved = useCallback((applicationUuid: string) => {
    setData((prevData) => ({
      ...prevData,
      resolvedInitialRouteByApplication: {
        ...(prevData.resolvedInitialRouteByApplication ?? {}),
        [applicationUuid]: true,
      },
    }));
  }, []);

  const clearData = useCallback(() => {
    setData(getInitialStoreData());
  }, []);

  const clearApplicationData = useCallback(() => {
    setData((prevData) => ({
      applicationMetadata: prevData.applicationMetadata,
      selectedProduct: prevData.selectedProduct,
      eligibilitySelection: prevData.eligibilitySelection,
      resolvedInitialRouteByApplication: {},
    }));
  }, []);

  useEffect(() => {
    setIsInitialNavigation(false);
  }, []);

  const value = useMemo(
    () => ({
      data,
      updateData,
      replaceData,
      markInitialRouteResolved,
      clearData,
      clearApplicationData,
      isInitialNavigation,
    }),
    [
      data,
      updateData,
      replaceData,
      markInitialRouteResolved,
      clearData,
      clearApplicationData,
      isInitialNavigation,
    ]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
